import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Church, Lock, ArrowRight, MessageCircleReply, Music2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GOSPEL_COURSES, GOSPEL_PRICE, GOSPEL_PAIR_PRICE } from "@/data/curriculum";

export const gospelTotal = (n) => GOSPEL_PAIR_PRICE * Math.floor(n / 2) + GOSPEL_PRICE * (n % 2);
const PRICE_LINE = `£${GOSPEL_PRICE} per course, or any two Gospel courses for £${GOSPEL_PAIR_PRICE} (mix levels or instruments)`;

export const GospelCard = ({ instrument }) => {
  if (!instrument.miniCourses?.length) return null;
  return (
    <div id="gospel" className="mt-6 scroll-mt-24 rounded-3xl border border-primary/40 bg-primary/5 p-6 sm:p-8" data-testid={`gospel-card-${instrument.id}`}>
      <div className="flex gap-4">
        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-primary/15 text-primary"><Church size={22} /></span>
        <div>
          <p className="font-mono text-[11px] uppercase tracking-widest text-primary" data-testid={`gospel-card-price-${instrument.id}`}>Gospel courses · Beginner & Intermediate · 6 lessons each</p>
          <p className="mt-1 text-sm text-muted-foreground">{PRICE_LINE}.</p>
          <p className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground"><Lock size={12} /> Available once you've bought any standard course</p>
          <Link to="/gospel-licks" className="mt-2 inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" data-testid={`gospel-licks-link-${instrument.id}`}><Music2 size={14} /> Gospel Lick Library <ArrowRight size={13} /></Link>
        </div>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        {instrument.miniCourses.map((course) => (
          <div key={course.id} className="flex flex-col justify-between gap-4 rounded-2xl border border-border bg-card p-5" data-testid={`gospel-course-${instrument.id}-${course.id}`}>
            <div>
              <h3 className="font-serif text-xl">{course.title}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{course.blurb}</p>
            </div>
            <Link to={`/courses/${instrument.id}/${course.id}`} data-testid={`open-${course.id}-${instrument.id}`}>
              <Button className="rounded-full bg-primary px-6 text-primary-foreground hover:bg-primary/90">View course <ArrowRight size={15} className="ml-2" /></Button>
            </Link>
          </div>
        ))}
      </div>
    </div>
  );
};

export const GospelUnlock = ({ courseKey }) => {
  const { user, purchases } = useAuth();
  const owned = new Set(purchases || []);
  const options = GOSPEL_COURSES.filter((c) => !owned.has(c.key));
  const [eligible, setEligible] = useState(null);
  const [busy, setBusy] = useState(false);
  const [picked, setPicked] = useState(courseKey ? [courseKey] : []);
  useEffect(() => {
    if (!user) return;
    const load = (tries) => api.get("/gospel/eligibility").then(({ data }) => setEligible(data.eligible)).catch(() => (tries > 0 ? setTimeout(() => load(tries - 1), 1500) : setEligible(null)));
    load(2);
  }, [user, purchases]);
  const toggle = (k) => setPicked((p) => (p.includes(k) ? p.filter((x) => x !== k) : [...p, k]));
  const total = gospelTotal(picked.length);
  const buy = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/gospel-checkout", { course_keys: picked, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); setBusy(false); }
  };
  return (
    <div className="mt-6 rounded-2xl border border-primary/40 bg-primary/10 p-5" data-testid="gospel-unlock">
      <div className="flex items-start gap-3">
        <Lock size={20} className="mt-0.5 shrink-0 text-primary" />
        <p className="text-sm text-muted-foreground" data-testid="gospel-unlock-message">
          {!user ? `Log in to unlock the Gospel courses: ${PRICE_LINE}.` : eligible === false
            ? `Buy any standard course to unlock the Gospel courses: ${PRICE_LINE}.`
            : <>{PRICE_LINE}. Sold separately, not part of course bundles.</>}
        </p>
      </div>
      {user && eligible && (
        <>
          <div className="mt-4 grid gap-2 sm:grid-cols-2">
            {options.map((c) => (
              <label key={c.key} className="flex cursor-pointer items-center gap-2 rounded-xl border border-border bg-card px-3 py-2 text-sm" data-testid={`gospel-pick-${c.key.replace(":", "-")}`}>
                <input type="checkbox" checked={picked.includes(c.key)} onChange={() => toggle(c.key)} className="accent-[hsl(var(--primary))]" />
                {c.title}
              </label>
            ))}
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm" data-testid="gospel-total">{picked.length ? <>Total: <span className="font-semibold">£{total}</span>{picked.length >= 2 && <span className="text-muted-foreground"> (pair pricing applied)</span>}</> : "Choose one or more Gospel courses."}</p>
            <Button onClick={buy} disabled={busy || !picked.length} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90" data-testid="gospel-buy-button">{busy ? "Opening checkout…" : picked.length ? `Unlock for £${total}` : "Choose a course"}</Button>
          </div>
        </>
      )}
      {!user && <Link to="/login" className="mt-4 inline-block" data-testid="gospel-login-button"><Button className="rounded-full bg-primary text-primary-foreground">Log in</Button></Link>}
    </div>
  );
};

export const MelodyFirst = () => (
  <div className="mt-6 flex items-start gap-3 rounded-2xl border border-border bg-card p-5" data-testid="melody-first">
    <Music2 size={20} className="mt-0.5 shrink-0 text-primary" />
    <p className="text-sm text-muted-foreground">
      <span className="font-medium text-foreground">Melody first. </span>
      The melody is the most important part of any song — it's what people remember and sing along to. Every lesson in this course shows you how to read, hear and support the melody, whether you're singing it, playing it or accompanying it.
    </p>
  </div>
);

export const DashboardAlerts = ({ eligibleForGospel, ownsGospel }) => {
  const [unread, setUnread] = useState(null);
  useEffect(() => { api.get("/tutor-questions/unread").then(({ data }) => setUnread(data)).catch(() => {}); }, []);
  return (
    <div className="mt-6 space-y-3">
      {unread?.courses?.map((c) => (
        <Link key={`${c.instrument}-${c.course_id}`} to={`/courses/${c.instrument}/${c.course_id}`} className="flex items-center gap-3 rounded-2xl border border-primary/40 bg-primary/10 p-4 transition-colors hover:bg-primary/15" data-testid={`reply-alert-${c.instrument}-${c.course_id}`}>
          <MessageCircleReply size={20} className="text-primary" />
          <p className="text-sm"><span className="font-medium">Jason replied to {c.count === 1 ? "your question" : `${c.count} of your questions`}</span> <span className="text-muted-foreground">· {c.course}</span></p>
          <ArrowRight size={15} className="ml-auto text-primary" />
        </Link>
      ))}
      {eligibleForGospel && !ownsGospel && (
        <Link to="/payment#gospel" className="flex items-center gap-3 rounded-2xl border border-border bg-card p-4 transition-colors hover:border-primary" data-testid="gospel-unlocked-alert">
          <Church size={20} className="text-primary" />
          <p className="text-sm"><span className="font-medium">You've unlocked the Gospel courses!</span> <span className="text-muted-foreground">· Beginner and Intermediate, piano and guitar — {PRICE_LINE}</span></p>
          <ArrowRight size={15} className="ml-auto text-primary" />
        </Link>
      )}
    </div>
  );
};
